import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import format from "date-fns/format";
import endOfDay from "date-fns/endOfDay";
import startOfDay from "date-fns/startOfDay";
import { AxnAdminUser } from "@idw-types/data/axn-admin-user";
import { ApplicationState } from "@idw-types/state/application-state";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { getActivityLogsRequest } from "@requests/reports/get-activity-logs";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { FormattedActivityLog } from "@idw-types/data/reports";
import { resources } from "@constants/hardcoded-strings";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { SERVER_DATE_TIME_FORMAT } from "@constants/date-formats";
import { formatActivityLogs } from "@formatters/activity-logs/activity-log-formatter";
import { logAndDisplayErrorModal } from "@utils/app-alert-util";
import { OrganizationActivitySearchFiltersState } from "@idw-types/state/organization-activity-state";
import { initialOrganizationActivityState } from "@redux/reducers/organization-activity-reducer";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { resetTableSettingsTableState } from "@redux/actions/table-settings-actions";
import { ActivityLogSearchForm } from "../../../common/activity-log-search-form/activity-log-search-form";
import { ActivityLogTableContainer } from "../../../common/activity-log-table-container/activity-log-table-container";

type OrganizationUserActivityContainerProps = {
    axnAdminUser: AxnAdminUser
};

export const OrganizationUserActivityContainer = (props: OrganizationUserActivityContainerProps) => {
    const dispatch = useDispatch();
    const { axnAdminUser } = props;

    const {
        organizationUserActivitesTableState
    } = useSelector((state: ApplicationState) => ({
        organizationUserActivitesTableState: state.tableSettings.organizationUserActivities
    }));

    useEffect(() => {
        dispatch(resetTableSettingsTableState(TableSettingsTableTypes.ORGANIZATION_USER_ACTIVITIES));
    }, [])

    const [activityLogs, setActivityLogs] = useState<Array<FormattedActivityLog>>(null);

    const [searchFilters, setSearchFilters] = useState<OrganizationActivitySearchFiltersState>({
        companyId: initialOrganizationActivityState.searchFilters.companyId,
        startDate: initialOrganizationActivityState.searchFilters.startDate,
        endDate: initialOrganizationActivityState.searchFilters.endDate,
        timeRangeType: initialOrganizationActivityState.searchFilters.timeRangeType
    });
    const [requestInProgress, setRequestInProgress] = useState(false);

    const handleSearch = async (newSearchFilters: OrganizationActivitySearchFiltersState) => {
        const { startDate, endDate } = newSearchFilters;

        dispatch(resetTableSettingsTableState(TableSettingsTableTypes.ORGANIZATION_USER_ACTIVITIES));
        setSearchFilters(newSearchFilters);

        setRequestInProgress(true);
        setActivityLogs(null);
        const { data, error } = await getActivityLogsRequest(
            axnAdminUser.id,
            null,
            format(startOfDay(startDate), SERVER_DATE_TIME_FORMAT),
            format(endOfDay(endDate), SERVER_DATE_TIME_FORMAT),
            Intl.DateTimeFormat().resolvedOptions().timeZone
        );
        if (data?.activityLogs) {
            const formattedActivityLogs = formatActivityLogs(data.activityLogs);
            setActivityLogs(formattedActivityLogs);
        } else if (error) {
            logAndDisplayErrorModal(
                dispatch,
                error,
                resources.requests.getActivityLogs.error.title,
                resources.requests.getActivityLogs.error.message
            );
        }
        setRequestInProgress(false);
    };

    return (
        <div>
            <ContentPageHeader title={resources.pages.organizationUserDetails.tabs.activity.title} />
            <ContentPageBody isMaxWidth={true}>
                <ActivityLogSearchForm
                    companies={[]}
                    requestInProgress={requestInProgress}
                    searchFilters={searchFilters}
                    handleSearch={handleSearch}
                />
                {!requestInProgress && activityLogs && (
                    <ActivityLogTableContainer
                        activityLogs={activityLogs}
                        tableState={organizationUserActivitesTableState}
                        isPageHeight={false}
                        tableSettingsTableType={TableSettingsTableTypes.ORGANIZATION_USER_ACTIVITIES}
                    />
                )}
                {requestInProgress && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </div>
    )
};